import { Platform } from '../types';
import type { WindowWithCapacitor } from '../dom-globals';

// window.tizen e window.webOS já são tipados via `declare global` nos
// arquivos TizenPlatformService.ts e WebOSPlatformService.ts. Capacitor
// vem do dom-globals.ts. PalmSystem (webOS legacy) é tipado inline porque
// só é checado aqui — não vale criar global pra um único uso.
type WindowMaybeWebOSLegacy = Window & { PalmSystem?: unknown };
// `window.__effluxEmbedded` é injetado pelo APK host (via AAR/WebView) quando o
// player roda EMBEDDED dentro de outro app. Checado só aqui — não vale criar
// global pra um único uso.
type WindowMaybeEmbedded = Window & { __effluxEmbedded?: unknown };
// Tauri v2 injeta `window.isTauri` e `window.__TAURI_INTERNALS__` em toda janela
// que ele controla (inclusive quando o WebView aponta pra uma URL remota).
// Checado só aqui — não vale criar global pra um único uso.
type WindowMaybeTauri = Window & {
  isTauri?: boolean;
  __TAURI_INTERNALS__?: unknown;
  __TAURI__?: unknown;
};

/**
 * Desktop (Tauri wrapper): distingue Windows de Linux pelo user agent do
 * WebView (WebView2 = "Windows NT"; WebKitGTK = "Linux" sem "Android").
 * macOS está fora do V1 (plano §11). Só é chamado depois de já ter descartado
 * embedded/tizen/webos/capacitor.
 */
function detectDesktopPlatform(): 'windows' | 'linux' | null {
  const w = window as WindowMaybeTauri;
  const isTauri =
    w.isTauri === true || !!w.__TAURI_INTERNALS__ || !!w.__TAURI__;
  if (!isTauri) return null;

  const ua = navigator.userAgent || '';
  if (/Linux/i.test(ua) && !/Android/i.test(ua)) return 'linux';
  // Windows é o alvo primário V1; UA inconclusivo também cai aqui.
  return 'windows';
}

export function detectPlatform(): Platform {
  if (typeof window === 'undefined') {
    return 'browser';
  }

  // Check for Embedded (rodando dentro de um APK host via WebView + AAR).
  // Checado ANTES de tudo: um WebView embedded não tem tizen/webOS/Capacitor e
  // cairia em 'browser'. O host injeta window.__effluxEmbedded pra marcar o modo.
  if ((window as WindowMaybeEmbedded).__effluxEmbedded) {
    return 'embedded';
  }

  // Check for Tizen (Samsung)
  if (window.tizen) {
    return 'tizen';
  }

  // Check for webOS (LG)
  if (window.webOS || (window as WindowMaybeWebOSLegacy).PalmSystem) {
    return 'webos';
  }

  // Check for Capacitor (Android/iOS)
  const cap = (window as WindowWithCapacitor).Capacitor;
  if (cap) {
    try {
      const platform = cap.getPlatform?.();
      if (platform === 'ios') {
        return 'ios';
      }
      if (platform === 'android') {
        return 'android';
      }
    } catch {
      // ignore and fallback to browser
    }
  }

  // Check for Desktop (Tauri wrapper — Windows/Linux/macOS).
  // Depois de Capacitor porque o desktop não tem tizen/webOS/Capacitor e
  // cairia em 'browser'.
  const desktop = detectDesktopPlatform();
  if (desktop) {
    return desktop;
  }

  // Default to browser
  return 'browser';
}

export function getPlatformFromEnv(): Platform | null {
  const envPlatform = import.meta.env.VITE_PLATFORM;
  if (
    envPlatform === 'tizen' ||
    envPlatform === 'webos' ||
    envPlatform === 'browser' ||
    envPlatform === 'android' ||
    envPlatform === 'ios' ||
    envPlatform === 'embedded' ||
    envPlatform === 'windows' ||
    envPlatform === 'linux'
  ) {
    return envPlatform;
  }
  return null;
}
